if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Dots are built here, before the thumbnails guard below, so they still work on layouts
        // and products that render no thumbnail strip.
        this.elements.dots = this.querySelector('[data-gallery-dots]');
        this.buildDots();

        // Bound before the thumbnails guard so swiping keeps the dots in sync even when no
        // thumbnail strip is rendered (mobile with thumbnails off, single-media layouts).
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement.dataset.mediaId;
        this.setActiveDot(mediaId);
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      // Build one dot per slide actually rendered in the viewer, so the dots stay 1:1 with the
      // gallery without duplicating the snippet's hide_variants / limit filtering.
      buildDots() {
        const dots = this.elements.dots;
        if (!dots) return;

        // Scope to the slides themselves: the lightbox zoom button inside each slide also
        // carries data-media-id, so a bare [data-media-id] returns two nodes per image.
        const slides = this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]');
        dots.querySelectorAll('.gallery-dot').forEach((dot) => dot.remove());
        if (slides.length < 2) {
          dots.setAttribute('hidden', '');
          return;
        }
        dots.removeAttribute('hidden');

        slides.forEach((slide, index) => {
          const mediaId = slide.dataset.mediaId;
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'slider-counter__link slider-counter__link--dots gallery-dot link';
          dot.dataset.target = mediaId;
          dot.setAttribute('aria-controls', this.elements.viewer.id);
          dot.setAttribute(
            'aria-label',
            window.accessibilityStrings?.imageAvailable
              ? window.accessibilityStrings.imageAvailable.replace('[index]', index + 1)
              : `${index + 1}`
          );
          if (slide.classList.contains('is-active')) {
            dot.classList.add('slider-counter__link--active');
            dot.setAttribute('aria-current', 'true');
          }
          dot.innerHTML = '<span class="dot"></span>';
          dot.addEventListener('click', this.setActiveMedia.bind(this, mediaId));
          dots.appendChild(dot);
        });
      }

      setActiveDot(mediaId) {
        if (!this.elements.dots) return;
        this.elements.dots.querySelectorAll('.gallery-dot').forEach((dot) => {
          const active = dot.dataset.target === mediaId;
          dot.classList.toggle('slider-counter__link--active', active);
          if (active) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }

      setActiveMedia(mediaId) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        // Resolve from the element, not the argument — setActiveMedia falls back to the first
        // slide when the requested id is missing (e.g. after a variant re-render).
        this.setActiveDot(activeMedia.dataset.mediaId);

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
